import { NgClass } from '@angular/common';
import { Component, input } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { RiskLevelColour } from '@shared/types';

/**
 * @title Risk Levels Legend Component
 * @description Displays a legend for risk levels with dynamic styling based on the risk level.
 */
@UntilDestroy()
@Component({
  selector: 'dxc-risk-levels-legend',
  imports: [NgClass],
  template: `
    <!-- Container for the risk levels legend. Applies dynamic class based on loading state. -->
    <div
      class="mb-6 mt-4 flex items-center gap-x-1"
      [ngClass]="{ 'loading-div': loading() }"
    >
      <!-- Loop through each risk level in the legend. -->
      @for (legendKey of riskLevelsLegend; track legendKey.valueNow) {
        <div class="legend-key flex flex-1 flex-col items-center gap-1">
          <!-- Displays the risk level label and values. Adjusts layout based on the valueNow property. -->
          <div
            class="flex w-full gap-x-1 px-2 text-center text-xs font-semibold"
            [ngClass]="{
              'justify-center': legendKey.valueNow === 3,
              'justify-between': legendKey.valueNow !== 3
            }"
          >
            <!-- Conditionally rendered max value based on the valueNow property. -->
            <span>{{ legendKey.minValue }}</span>
            <span>{{ legendKey.label }}</span>
            @if (legendKey.valueNow !== 3) {
              <span>{{ legendKey.maxValue }}</span>
            }
          </div>

          <!-- Visual representation of the risk level as a progress bar. -->
          <div
            class="flex h-2.5 w-full justify-center overflow-hidden whitespace-nowrap rounded-full {{
              legendKey.colour
            }}"
            role="progressbar"
            aria-valuemax="100"
            aria-valuemin="0"
            [attr.aria-valuenow]="legendKey.valueNow"
            [ngClass]="{ 'loading-div': loading() }"
          ></div>
        </div>
      }
    </div>
  `
})
export class RiskLevelsLegendComponent {
  public loading = input.required<boolean>();

  // Defines the risk levels and their properties for display in the legend.
  riskLevelsLegend = [
    {
      colour: RiskLevelColour.low,
      label: 'Low',
      minValue: 1,
      maxValue: 2,
      valueNow: 2
    },
    {
      colour: RiskLevelColour.medium,
      label: 'Medium',
      minValue: 3,
      maxValue: 3,
      valueNow: 3
    },
    {
      colour: RiskLevelColour.high,
      label: 'High',
      minValue: 4,
      maxValue: 5,
      valueNow: 5
    }
  ];
}
